
export interface MetadataConfig {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  favicon: string;
  keywords: string;
  canonicalUrl: string;
}

const DEFAULT_METADATA: MetadataConfig = {
  title: "Growth OS",
  description: "AI-powered growth and optimization platform for agencies",
  ogTitle: "Growth OS - AI Growth Platform",
  ogDescription: "Optimize your agency's growth with AI-powered analysis, SEO tools, and growth hacking strategies",
  ogImage: "",
  favicon: "/lovable-uploads/9402095b-6cef-4136-9d8b-7920c720af4a.png",
  keywords: "growth agency, AI, optimization, SEO, growth hacking, agency tools",
  canonicalUrl: ""
};

const STORAGE_KEY = 'saio_global_metadata';

// Helper functions for localStorage
const saveToLocalStorage = (metadata: MetadataConfig) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(metadata));
    }
  } catch (error) {
    console.error('Error saving metadata to localStorage:', error);
  }
};

const loadFromLocalStorage = (): MetadataConfig => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_METADATA, ...parsed };
      }
    }
  } catch (error) {
    console.error('Error loading metadata from localStorage:', error);
  }
  return { ...DEFAULT_METADATA };
};

// Helper functions for DOM manipulation
const createOrUpdateMetaTag = (name: string, content: string) => {
  if (!content || typeof document === 'undefined') return;
  
  let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const createOrUpdateCanonicalLink = (canonicalUrl: string) => {
  if (typeof document === 'undefined') return;
  
  // Remove existing canonical link
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.remove();
  }
  
  // Use current URL if no canonical URL is provided
  const finalCanonicalUrl = canonicalUrl || window.location.href;
  
  // Add new canonical link
  const link = document.createElement('link');
  link.rel = 'canonical';
  link.href = finalCanonicalUrl;
  document.head.appendChild(link);
};

const createOrUpdateFavicon = (faviconUrl: string) => {
  if (!faviconUrl || typeof document === 'undefined') return;
  
  try {
    // Remove ALL existing favicon links
    const existingLinks = document.querySelectorAll('link[rel*="icon"]');
    existingLinks.forEach(link => {
      try {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      } catch (error) {
        console.warn('Error removing favicon link:', error);
      }
    });
    
    // Create new favicon link with cache-busting
    const link = document.createElement('link');
    link.rel = 'icon';
    
    // Add cache-busting parameter
    const cacheBuster = `?v=${Date.now()}`;
    const finalFaviconUrl = faviconUrl.includes('?') 
      ? `${faviconUrl}&v=${Date.now()}` 
      : `${faviconUrl}${cacheBuster}`;
    
    link.href = finalFaviconUrl;
    
    // Determine type based on URL
    if (faviconUrl.includes('.png')) {
      link.type = 'image/png';
    } else if (faviconUrl.includes('.jpg') || faviconUrl.includes('.jpeg')) {
      link.type = 'image/jpeg';
    } else if (faviconUrl.includes('.svg')) {
      link.type = 'image/svg+xml';
    } else {
      link.type = 'image/x-icon';
    }
    
    // Add to head
    if (document.head) {
      document.head.appendChild(link);
    }
    
    // Additional techniques to force favicon update
    setTimeout(() => {
      try {
        // Force browser to re-read the favicon
        const newLink = link.cloneNode(true) as HTMLLinkElement;
        newLink.href = finalFaviconUrl + '&force=' + Math.random();
        if (link.parentNode && document.head) {
          document.head.removeChild(link);
          document.head.appendChild(newLink);
        }
      } catch (error) {
        console.warn('Error updating favicon:', error);
      }
    }, 100);
  } catch (error) {
    console.error('Error in createOrUpdateFavicon:', error);
  }
};

const updateDocumentHead = (metadata: MetadataConfig) => {
  if (typeof document === 'undefined') return;
  
  // Update title
  document.title = metadata.title;
  
  // Update meta tags
  createOrUpdateMetaTag('description', metadata.description);
  createOrUpdateMetaTag('keywords', metadata.keywords);
  createOrUpdateMetaTag('og:title', metadata.ogTitle);
  createOrUpdateMetaTag('og:description', metadata.ogDescription);
  createOrUpdateMetaTag('og:type', 'website');
  if (metadata.ogImage) {
    createOrUpdateMetaTag('og:image', metadata.ogImage);
  }
  createOrUpdateMetaTag('twitter:card', 'summary_large_image');
  createOrUpdateMetaTag('twitter:title', metadata.ogTitle);
  createOrUpdateMetaTag('twitter:description', metadata.ogDescription);
  if (metadata.ogImage) {
    createOrUpdateMetaTag('twitter:image', metadata.ogImage);
  }
  
  // Update canonical link
  createOrUpdateCanonicalLink(metadata.canonicalUrl);
  
  // Update favicon - always call this
  createOrUpdateFavicon(metadata.favicon);
};

// Global metadata store
let globalMetadata: MetadataConfig = { ...DEFAULT_METADATA };

// Initialize metadata immediately when the script loads
if (typeof window !== 'undefined') {
  // Load from localStorage
  globalMetadata = loadFromLocalStorage();
  
  // Auto-populate canonical URL with current URL if it's empty
  if (!globalMetadata.canonicalUrl) {
    globalMetadata.canonicalUrl = window.location.href;
    saveToLocalStorage(globalMetadata);
  }
  
  // Apply metadata immediately - no need to wait for DOM
  const applyInitialMetadata = () => {
    updateDocumentHead(globalMetadata);
  };
  
  // Apply immediately if DOM is ready, otherwise wait for it
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyInitialMetadata);
  } else {
    applyInitialMetadata();
  }
}

export const getGlobalMetadata = (): MetadataConfig => {
  if (typeof window !== 'undefined') {
    globalMetadata = loadFromLocalStorage();
    
    if (!globalMetadata.canonicalUrl) {
      globalMetadata.canonicalUrl = window.location.href;
    }
  }
  return globalMetadata;
};

export const setGlobalMetadata = (metadata: MetadataConfig): void => {
  const finalMetadata = {
    ...metadata,
    canonicalUrl: metadata.canonicalUrl || (typeof window !== 'undefined' ? window.location.href : '')
  };
  
  globalMetadata = finalMetadata;
  
  saveToLocalStorage(finalMetadata);
  updateDocumentHead(finalMetadata);
  
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('metadataUpdated', { detail: finalMetadata }));
  }
};
